import { useEffect, useState } from "react"
import { DateTime } from "luxon"
import { Clock, Trash2, Calendar, AlertCircle } from "lucide-react"
import { Tabs, TabsContent } from "@/components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import InputHours from "@/components/InputHours/InputHours"
import Confirm from "@/components/ui/confirm"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/hooks/use-toast"
import { useIsUserAdmin } from "@/hooks/authHelpers"
import axios from "@/lib/axios"
import Holidays from "date-holidays"
import { useParams } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

const hd = new Holidays("US")

const daysOfWeek = [
    { id: "monday", label: "Monday", value: 1 },
    { id: "tuesday", label: "Tuesday", value: 2 },
    { id: "wednesday", label: "Wednesday", value: 3 },
    { id: "thursday", label: "Thursday", value: 4 },
    { id: "friday", label: "Friday", value: 5 },
    { id: "saturday", label: "Saturday", value: 6 },
    { id: "sunday", label: "Sunday", value: 0 },
]

const formatTime = (time) => DateTime.fromISO(time).toLocaleString(DateTime.TIME_SIMPLE)

export default function BusinessHoursTab() {
    const { id: communityId } = useParams()
    const [businessHours, setBusinessHours] = useState(null)
    const [settings, setSettings] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [expandedDays, setExpandedDays] = useState([])
    const { toast } = useToast()
    const isAdmin = useIsUserAdmin()

    const currentYear = new Date().getFullYear()
    const federalHolidays = hd
        .getHolidays(currentYear)
        .filter(({ type }) => type === "public")
        .map((holiday) => holiday.name)

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [hoursResponse, settingsResponse] = await Promise.all([
                    axios.get(`/communities/${communityId}/business-hours`),
                    axios.get(`/communities/${communityId}/settings`),
                ])
                setBusinessHours(hoursResponse.data)
                setSettings(settingsResponse.data)
            } catch (error) {
                console.error("Error fetching data:", error)
                toast({
                    title: "Error",
                    description: "Failed to load business hours. Please try again.",
                    variant: "destructive",
                })
            } finally {
                setIsLoading(false)
            }
        }

        fetchData()
    }, [communityId, toast, isAdmin])

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/business-hours/${id}`)
            setBusinessHours(businessHours.filter((h) => h.id !== id))
            toast({
                title: "Success",
                description: "Business hours deleted successfully.",
            })
        } catch (error) {
            console.error("Error deleting business hours:", error)
            toast({
                title: "Error",
                description: "Failed to delete business hours. Please try again.",
                variant: "destructive",
            })
        }
    }

    const handleHolidayToggle = async (checked) => {
        setIsLoading(true)
        try {
            await axios.patch(`/communities/${communityId}/settings`, {
                ...settings?.should_announce_company_when_calling,
                is_calls_disabled_federal_holidays: checked,
            })
            setSettings({ ...settings, is_calls_disabled_federal_holidays: checked })
            toast({
                title: "Success",
                description: "Federal holiday setting updated successfully.",
            })
        } catch (error) {
            console.error("Error updating federal holiday setting:", error)
            toast({
                title: "Error",
                description: "Failed to update federal holiday setting. Please try again.",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    const toggleAccordion = (dayId) => {
        setExpandedDays((prev) => (prev.includes(dayId) ? prev.filter((id) => id !== dayId) : [...prev, dayId]))
    }

    const renderHoursForDay = (day) => {
        const hoursForDay = businessHours?.filter((hour) => hour.day_of_week === day.value) || []
        const isExpanded = expandedDays.includes(day.id)

        return (
            <AccordionItem key={day.id} value={day.id} className="border rounded-md mb-2 overflow-hidden">
                <AccordionTrigger
                    onClick={() => toggleAccordion(day.id)}
                    className={`px-4 py-3 hover:no-underline ${isExpanded ? "bg-gray-50" : ""}`}
                >
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span className="font-medium">{day.label}</span>
                        </div>
                        <div className="flex items-center">
                            {hoursForDay.length > 0 ? (
                                <Badge variant="outline" className="mr-4 bg-green-50 text-green-700 border-green-200">
                                    {hoursForDay.length} {hoursForDay.length === 1 ? "Schedule" : "Schedules"}
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="mr-4 bg-gray-50 text-gray-500 border-gray-200">
                                    No Hours Set
                                </Badge>
                            )}
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pt-2 pb-4 bg-white">
                    {hoursForDay.length === 0 ? (
                        <div className="py-2 text-sm text-muted-foreground mb-4">No business hours set for {day.label}</div>
                    ) : (
                        <div className="space-y-2 mb-4">
                            {hoursForDay.map((hour) => (
                                <div key={hour.id} className="flex items-center justify-between p-2 rounded-md border bg-gray-50">
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-gray-400" />
                                        <span className="font-medium">
                                            {formatTime(hour.open_time)} - {formatTime(hour.close_time)}
                                        </span>
                                    </div>
                                    {isAdmin && <Confirm
                                        onConfirm={() => handleDelete(hour.id)}
                                        title="Delete Business Hours"
                                        description="Are you sure you want to delete these business hours? This action cannot be undone."
                                        confirmText="Delete"
                                    >
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={!isAdmin}
                                            className="h-8 w-8 text-gray-500 hover:text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </Confirm>}
                                </div>
                            ))}
                        </div>
                    )}

                    {isAdmin && (
                        <div className="bg-gray-50 p-3 rounded-md">
                            <div className="text-sm font-medium mb-2">Add Hours for {day.label}</div>
                            <InputHours
                                dayOfWeek={day.value}
                                communityId={communityId}
                                onCreation={(newHours) => {
                                    setBusinessHours([...businessHours, newHours])
                                    toast({
                                        title: "Success",
                                        description: `Business hours added for ${day.label}.`,
                                    })
                                }}
                                disabled={!isAdmin}
                            />
                        </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        )
    }

    if (isLoading && !businessHours) {
        return (
            <Tabs value="hours">
                <TabsContent value="hours">
                    <Card>
                        <CardHeader>
                            <Skeleton className="h-8 w-3/4 mb-2" />
                            <Skeleton className="h-4 w-1/2" />
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {[...Array(7)].map((_, index) => (
                                <Skeleton key={index} className="h-16 w-full" />
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        )
    }

    if (!businessHours) {
        return (
            <Tabs value="hours">
                <TabsContent value="hours">
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>
                            Could not load business hours. Please refresh the page or contact support.
                        </AlertDescription>
                    </Alert>
                </TabsContent>
            </Tabs>
        )
    }

    return (
        <TabsContent value="hours">
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="flex gap-2 items-center">
                            <Calendar className="h-5 w-5 text-gray-500" />
                            <h4 className="font-medium">Federal Holiday Settings</h4>
                        </div>
                        <p className="text-sm text-gray-500">Automatically suspend outbound calling on U.S. federal holidays.</p>
                    </div>
                    <Switch
                        checked={settings?.is_calls_disabled_federal_holidays}
                        onCheckedChange={handleHolidayToggle}
                        disabled={!isAdmin || isLoading}
                    />
                </div>
                <div className="text-sm text-gray-600">
                    <div className="flex flex-wrap gap-2">
                        {federalHolidays.map((holiday) => (
                            <Badge key={holiday} variant="secondary" className="bg-gray-100">
                                {holiday}
                            </Badge>
                        ))}
                    </div>
                </div>
            </div>
            <Accordion type="multiple" value={expandedDays} onValueChange={setExpandedDays} className="w-full">
                {daysOfWeek.map(renderHoursForDay)}
            </Accordion>
        </TabsContent>
    )
}
